import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { MemoryItem, MemoryLocation, Session } from "../types";

// =============================================================================
// Types
// =============================================================================

interface PrivacyAuditProps {
  session: Session;
  memoryItems: MemoryItem[];
  onBurnAll: () => void;
  onBurnItem: (memoryId: string) => void;
}

// =============================================================================
// Helpers
// =============================================================================

function formatDuration(seconds: number): string {
  if (seconds <= 0) return "Expired";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const LOCATION_CONFIG: Record<
  MemoryLocation,
  { label: string; description: string; color: string; bgColor: string }
> = {
  device: {
    label: "Device",
    description: "Stored locally on this device",
    color: "text-hvme-primary",
    bgColor: "bg-hvme-primary/10",
  },
  redis: {
    label: "Redis",
    description: "Stored in ephemeral server cache",
    color: "text-hvme-gold",
    bgColor: "bg-hvme-gold/10",
  },
  pinned: {
    label: "Pinned",
    description: "Persistently stored, no TTL",
    color: "text-hvme-green",
    bgColor: "bg-hvme-green/10",
  },
};

const PRIVACY_MODE_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string; borderColor: string }
> = {
  standard: {
    label: "Standard",
    color: "text-hvme-primary",
    bgColor: "bg-hvme-primary/10",
    borderColor: "border-hvme-primary/30",
  },
  "high-sensitivity": {
    label: "High Sensitivity",
    color: "text-hvme-gold",
    bgColor: "bg-hvme-gold/10",
    borderColor: "border-hvme-gold/30",
  },
  offline: {
    label: "Offline",
    color: "text-hvme-text-secondary",
    bgColor: "bg-hvme-bg-surface",
    borderColor: "border-hvme-border",
  },
};

// =============================================================================
// Emergency Wipe Confirmation
// =============================================================================

function EmergencyWipeDialog({
  open,
  itemCount,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  itemCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [confirmText, setConfirmText] = useState("");
  const confirmed = confirmText.toLowerCase() === "burn";

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-hvme-bg-deep/90 backdrop-blur-md"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="glass-panel p-8 max-w-md w-full mx-4 border-hvme-red/30 rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Warning icon */}
        <div className="w-14 h-14 mx-auto mb-4 rounded-full border-2 border-hvme-red/40 bg-hvme-red/10 flex items-center justify-center">
          <span className="text-2xl text-hvme-red font-heading">!</span>
        </div>

        <h3 className="font-heading text-lg font-semibold text-hvme-text-primary text-center mb-2">
          Emergency Data Wipe
        </h3>
        <p className="font-body text-sm text-hvme-text-secondary text-center mb-6">
          This will immediately and irreversibly delete all {itemCount} data items
          across every storage location. Type{" "}
          <span className="font-mono text-hvme-red font-semibold">BURN</span> to
          confirm.
        </p>

        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder='Type "BURN" to confirm'
          autoFocus
          className="
            w-full mb-6 bg-hvme-bg-deep border border-hvme-red/30 rounded-xl
            px-4 py-3 text-sm text-center text-hvme-text-primary placeholder-hvme-text-muted
            font-mono uppercase tracking-widest
            focus:outline-none focus:border-hvme-red/60 focus:ring-1 focus:ring-hvme-red/20
            transition-all duration-200
          "
        />

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="font-body flex-1 px-4 py-2.5 text-sm text-hvme-text-secondary hover:text-hvme-text-primary rounded-xl border border-hvme-border hover:border-hvme-text-muted hover:shadow-[0_0_12px_rgba(255,255,255,0.05)] transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!confirmed}
            className="
              font-body flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200
              bg-hvme-red/20 text-hvme-red border border-hvme-red/40
              hover:bg-hvme-red/30 hover:shadow-[0_0_20px_rgba(255,74,90,0.25)]
              disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:shadow-none
            "
          >
            Wipe All Data
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// =============================================================================
// Data Inventory Section
// =============================================================================

function DataInventory({
  memoryItems,
  onBurnItem,
}: {
  memoryItems: MemoryItem[];
  onBurnItem: (id: string) => void;
}) {
  // Group items by location
  const grouped = useMemo(() => {
    const groups: Record<MemoryLocation, MemoryItem[]> = {
      device: [],
      redis: [],
      pinned: [],
    };
    for (const item of memoryItems) {
      groups[item.location].push(item);
    }
    return groups;
  }, [memoryItems]);

  const locations: MemoryLocation[] = ["device", "redis", "pinned"];

  return (
    <div className="space-y-4">
      <h3 className="font-heading text-xs font-medium uppercase tracking-widest text-hvme-text-muted">
        Data Inventory
      </h3>

      {locations.map((location) => {
        const items = grouped[location];
        const config = LOCATION_CONFIG[location];
        const totalSize = items.reduce((sum, i) => sum + i.sizeBytes, 0);

        return (
          <motion.div
            key={location}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: locations.indexOf(location) * 0.1 }}
            className="glass-panel overflow-hidden rounded-2xl hover:shadow-[0_0_20px_rgba(255,255,255,0.03)] transition-all duration-300"
          >
            {/* Location header */}
            <div className="flex items-center justify-between p-4 border-b border-hvme-border-subtle">
              <div className="flex items-center gap-3">
                <span
                  className={`w-2 h-2 rounded-full ${config.bgColor}`}
                  style={{
                    boxShadow: items.length > 0 ? `0 0 6px currentColor` : "none",
                  }}
                />
                <div>
                  <span className={`font-heading text-sm font-medium ${config.color}`}>
                    {config.label}
                  </span>
                  <p className="font-body text-[10px] text-hvme-text-muted">
                    {config.description}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className="font-body text-sm font-mono text-hvme-text-secondary">
                  {items.length}
                </span>
                <span className="font-body text-xs text-hvme-text-muted ml-1">
                  items
                </span>
                {totalSize > 0 && (
                  <p className="font-body text-[10px] text-hvme-text-muted font-mono">
                    {formatBytes(totalSize)}
                  </p>
                )}
              </div>
            </div>

            {/* Items */}
            {items.length > 0 ? (
              <div className="divide-y divide-hvme-border-subtle">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-hvme-bg-deep/30 transition-colors"
                  >
                    <div className="flex-1 min-w-0 mr-4">
                      <span className="font-body text-xs font-medium text-hvme-text-primary truncate block">
                        {item.key}
                      </span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="font-body text-[10px] text-hvme-text-muted font-mono">
                          {formatBytes(item.sizeBytes)}
                        </span>
                        {!item.pinned && (
                          <span className="font-body text-[10px] text-hvme-text-muted font-mono">
                            TTL: {formatDuration(item.remainingSeconds)}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => onBurnItem(item.id)}
                      className="font-body text-[10px] px-2 py-1 rounded-xl border border-hvme-red/20 text-hvme-red/60 hover:text-hvme-red hover:border-hvme-red/40 hover:shadow-[0_0_12px_rgba(255,74,90,0.15)] transition-all duration-200"
                    >
                      Burn
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center">
                <span className="font-body text-xs text-hvme-text-muted">
                  No data in this location
                </span>
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Privacy Audit (Main Component)
// =============================================================================

export function PrivacyAudit({
  session,
  memoryItems,
  onBurnAll,
  onBurnItem,
}: PrivacyAuditProps) {
  const [showEmergencyWipe, setShowEmergencyWipe] = useState(false);

  const modeConfig =
    PRIVACY_MODE_CONFIG[session.privacyMode] ?? PRIVACY_MODE_CONFIG.standard ?? {
      label: "Unknown",
      color: "text-hvme-text-secondary",
      bgColor: "bg-hvme-bg-surface",
      borderColor: "border-hvme-border",
    };

  // Compute statistics
  const stats = useMemo(() => {
    const totalItems = memoryItems.length;
    const totalSize = memoryItems.reduce((sum, i) => sum + i.sizeBytes, 0);
    const pinnedItems = memoryItems.filter((i) => i.pinned).length;
    const expiringItems = memoryItems.filter(
      (i) => !i.pinned && i.remainingSeconds < 60,
    ).length;
    const soonestExpiry = memoryItems
      .filter((i) => !i.pinned && i.remainingSeconds > 0)
      .reduce(
        (min, i) => Math.min(min, i.remainingSeconds),
        Infinity,
      );

    return { totalItems, totalSize, pinnedItems, expiringItems, soonestExpiry };
  }, [memoryItems]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-heading text-lg font-semibold text-hvme-text-primary">
            Privacy Audit
          </h2>
          <p className="font-body text-xs text-hvme-text-muted mt-1">
            Full visibility into what data exists, where it lives, and when it
            expires.
          </p>
        </div>

        {/* Privacy mode badge */}
        <div
          className={`
            px-4 py-2 rounded-xl border text-sm font-medium font-heading
            ${modeConfig.bgColor} ${modeConfig.color} ${modeConfig.borderColor}
            hover:shadow-[0_0_16px_rgba(255,255,255,0.05)] transition-all duration-200
          `}
        >
          {modeConfig.label}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Total Items"
          value={stats.totalItems.toString()}
          subValue={formatBytes(stats.totalSize)}
        />
        <SummaryCard
          label="Pinned"
          value={stats.pinnedItems.toString()}
          subValue={session.memoryPolicy.allowPinning ? "Allowed" : "Disabled"}
          accent={!session.memoryPolicy.allowPinning ? "red" : undefined}
        />
        <SummaryCard
          label="Expiring Soon"
          value={stats.expiringItems.toString()}
          subValue="< 60s remaining"
          accent={stats.expiringItems > 0 ? "gold" : undefined}
        />
        <SummaryCard
          label="Next Expiry"
          value={
            stats.soonestExpiry === Infinity
              ? "--"
              : formatDuration(stats.soonestExpiry)
          }
          subValue={
            stats.soonestExpiry === Infinity
              ? "No pending" : "auto-deletion"
          }
        />
      </div>

      {/* Session Policy Details */}
      <div className="glass-panel p-5 rounded-2xl">
        <h3 className="font-heading text-xs font-medium uppercase tracking-widest text-hvme-text-muted mb-4">
          Session Policy
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PolicyItem
            label="Default TTL"
            value={formatDuration(session.memoryPolicy.defaultTtlSeconds)}
          />
          <PolicyItem
            label="Pinning"
            value={session.memoryPolicy.allowPinning ? "Allowed" : "Disabled"}
            highlight={!session.memoryPolicy.allowPinning}
          />
          <PolicyItem
            label="Remote Storage"
            value={
              session.memoryPolicy.allowRemoteStorage ? "Enabled" : "Disabled"
            }
            highlight={!session.memoryPolicy.allowRemoteStorage}
          />
        </div>
      </div>

      {/* Data Inventory */}
      <DataInventory memoryItems={memoryItems} onBurnItem={onBurnItem} />

      {/* Emergency Wipe */}
      <div className="glass-panel p-5 border-hvme-red/10 rounded-2xl hover:shadow-[0_0_24px_rgba(255,74,90,0.08)] transition-all duration-300">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-sm font-semibold text-hvme-red">
              Emergency Wipe
            </h3>
            <p className="font-body text-xs text-hvme-text-muted mt-1">
              Immediately and irreversibly delete all data across all storage
              locations.
            </p>
          </div>
          <motion.button
            onClick={() => setShowEmergencyWipe(true)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            disabled={memoryItems.length === 0}
            className="
              font-heading px-6 py-2.5 text-sm font-bold rounded-xl transition-all duration-200
              bg-hvme-red/15 text-hvme-red border-2 border-hvme-red/40
              hover:bg-hvme-red/25 hover:border-hvme-red/60
              hover:shadow-[0_0_24px_rgba(255,74,90,0.2)]
              active:shadow-[0_0_32px_rgba(255,74,90,0.3)]
              disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:shadow-none
            "
          >
            Burn Now
          </motion.button>
        </div>
      </div>

      {/* Emergency Wipe Dialog */}
      <AnimatePresence>
        {showEmergencyWipe && (
          <EmergencyWipeDialog
            open={showEmergencyWipe}
            itemCount={memoryItems.length}
            onConfirm={() => {
              onBurnAll();
              setShowEmergencyWipe(false);
            }}
            onCancel={() => setShowEmergencyWipe(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function SummaryCard({
  label,
  value,
  subValue,
  accent,
}: {
  label: string;
  value: string;
  subValue: string;
  accent?: "gold" | "red";
}) {
  return (
    <div className="glass-panel p-4 rounded-2xl hover:shadow-[0_0_20px_rgba(255,255,255,0.04)] transition-all duration-300">
      <span className="font-heading text-[10px] font-medium uppercase tracking-widest text-hvme-text-muted">
        {label}
      </span>
      <p
        className={`font-heading text-2xl font-light mt-1 ${
          accent === "gold"
            ? "text-hvme-gold"
            : accent === "red"
              ? "text-hvme-red"
              : "text-hvme-text-primary"
        }`}
      >
        {value}
      </p>
      <span className="font-body text-[10px] text-hvme-text-muted">{subValue}</span>
    </div>
  );
}

function PolicyItem({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center justify-between p-3 rounded-xl bg-hvme-bg-deep/50 hover:bg-hvme-bg-deep/70 transition-colors duration-200">
      <span className="font-body text-xs text-hvme-text-muted">{label}</span>
      <span
        className={`font-body text-xs font-mono ${
          highlight ? "text-hvme-red" : "text-hvme-text-secondary"
        }`}
      >
        {value}
      </span>
    </div>
  );
}
